type CallActionType = 'accepted' | 'rejected' | 'ended';

type CallClaim = {
  tabId: string;
  expiresAt: number;
  visibilityState: 'visible' | 'hidden';
  updatedAt: number;
};

type CoordinationEvent =
  | {
      type: 'state';
      callSessionId: string;
      leaderTabId: string | null;
      isLeader: boolean;
    }
  | {
      type: 'action';
      callSessionId: string;
      action: CallActionType;
      fromTabId: string;
    };

const CHANNEL_NAME = 'videocall-call-control';
const CLAIM_PREFIX = 'videocall.callLeader.';
const ACTION_STORAGE_KEY = 'videocall.callActionEvent';
const CLAIM_LEASE_MS = 4000;
const CLAIM_HEARTBEAT_MS = 1500;

function createTabId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // ignore
  }
  return `tab_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function getClaimStorageKey(callSessionId: string): string {
  return `${CLAIM_PREFIX}${String(callSessionId || '').trim()}`;
}

function getVisibilityState(): 'visible' | 'hidden' {
  return document.visibilityState === 'visible' ? 'visible' : 'hidden';
}

function parseClaim(raw: string | null): CallClaim | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const tabId = String(parsed?.tabId || '').trim();
    const expiresAt = Number(parsed?.expiresAt || 0);
    const visibilityState = parsed?.visibilityState === 'visible' ? 'visible' : 'hidden';
    const updatedAt = Number(parsed?.updatedAt || 0);
    if (!tabId || !Number.isFinite(expiresAt) || expiresAt <= 0) return null;
    return { tabId, expiresAt, visibilityState, updatedAt };
  } catch {
    return null;
  }
}

function isClaimAlive(claim: CallClaim | null): boolean {
  return !!claim && claim.expiresAt > Date.now();
}

class CallTabCoordinationService {
  private readonly tabId = createTabId();
  private readonly listeners = new Set<(event: CoordinationEvent) => void>();
  private readonly channel =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;
  private managedCallSessionId: string | null = null;
  private heartbeatTimer: number | null = null;
  private lastStateKey = '';

  constructor() {
    this.channel?.addEventListener('message', (event) => {
      this.handleIncomingMessage(event.data);
    });

    window.addEventListener('storage', this.handleStorageEvent);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('pagehide', this.handlePageHide);
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  subscribe(listener: (event: CoordinationEvent) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getSnapshot(callSessionId: string | null | undefined): { leaderTabId: string | null; isLeader: boolean } {
    const normalized = String(callSessionId || '').trim();
    if (!normalized) return { leaderTabId: null, isLeader: false };
    const claim = this.readClaim(normalized);
    const leaderTabId = isClaimAlive(claim) ? claim!.tabId : null;
    return {
      leaderTabId,
      isLeader: leaderTabId === this.tabId
    };
  }

  startManagingIncomingCall(callSessionId: string): void {
    const normalized = String(callSessionId || '').trim();
    if (!normalized) return;

    if (this.managedCallSessionId && this.managedCallSessionId !== normalized) {
      this.stopManagingIncomingCall(this.managedCallSessionId);
    }

    this.managedCallSessionId = normalized;
    this.ensureClaimForManagedCall();

    if (this.heartbeatTimer === null) {
      this.heartbeatTimer = window.setInterval(() => {
        this.ensureClaimForManagedCall();
      }, CLAIM_HEARTBEAT_MS);
    }
  }

  stopManagingIncomingCall(callSessionId: string, options?: { keepClaim?: boolean }): void {
    const normalized = String(callSessionId || '').trim();
    if (!normalized) return;

    if (this.managedCallSessionId === normalized) {
      this.managedCallSessionId = null;
      if (this.heartbeatTimer !== null) {
        window.clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = null;
      }
    }

    if (!options?.keepClaim) {
      const claim = this.readClaim(normalized);
      if (claim?.tabId === this.tabId) {
        this.clearClaim(normalized);
      } else {
        this.emitState(normalized);
      }
    } else {
      this.emitState(normalized);
    }
  }

  noteCallAction(callSessionId: string, action: CallActionType): void {
    const normalized = String(callSessionId || '').trim();
    if (!normalized) return;

    this.broadcast({
      type: 'action',
      callSessionId: normalized,
      action,
      fromTabId: this.tabId
    });
  }

  private emit(event: CoordinationEvent): void {
    for (const listener of this.listeners) {
      listener(event);
    }
  }

  private emitState(callSessionId: string): void {
    const snapshot = this.getSnapshot(callSessionId);
    const stateKey = `${callSessionId}:${snapshot.leaderTabId || ''}:${snapshot.isLeader ? '1' : '0'}`;
    if (stateKey === this.lastStateKey) return;
    this.lastStateKey = stateKey;
    this.emit({
      type: 'state',
      callSessionId,
      leaderTabId: snapshot.leaderTabId,
      isLeader: snapshot.isLeader
    });
  }

  private readClaim(callSessionId: string): CallClaim | null {
    try {
      return parseClaim(localStorage.getItem(getClaimStorageKey(callSessionId)));
    } catch {
      return null;
    }
  }

  private writeClaim(callSessionId: string): void {
    const claim: CallClaim = {
      tabId: this.tabId,
      expiresAt: Date.now() + CLAIM_LEASE_MS,
      visibilityState: getVisibilityState(),
      updatedAt: Date.now()
    };

    try {
      localStorage.setItem(getClaimStorageKey(callSessionId), JSON.stringify(claim));
    } catch {
      // ignore
    }

    this.broadcast({
      type: 'state',
      callSessionId,
      leaderTabId: claim.tabId,
      isLeader: true
    });
  }

  private clearClaim(callSessionId: string): void {
    try {
      localStorage.removeItem(getClaimStorageKey(callSessionId));
    } catch {
      // ignore
    }

    this.broadcast({
      type: 'state',
      callSessionId,
      leaderTabId: null,
      isLeader: false
    });
  }

  private shouldStealClaim(existing: CallClaim | null): boolean {
    if (!existing || !isClaimAlive(existing)) return true;
    if (existing.tabId === this.tabId) return true;
    return getVisibilityState() === 'visible' && existing.visibilityState !== 'visible';
  }

  private ensureClaimForManagedCall(): void {
    const callSessionId = this.managedCallSessionId;
    if (!callSessionId) return;

    const existing = this.readClaim(callSessionId);
    if (this.shouldStealClaim(existing)) {
      this.writeClaim(callSessionId);
    } else {
      this.emitState(callSessionId);
    }
  }

  private broadcast(event: CoordinationEvent): void {
    if (event.type === 'state') {
      this.emit(event);
    }

    try {
      this.channel?.postMessage(event);
    } catch {
      // ignore
    }

    if (event.type === 'action') {
      try {
        localStorage.setItem(
          ACTION_STORAGE_KEY,
          JSON.stringify({
            ...event,
            eventId: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
          })
        );
        localStorage.removeItem(ACTION_STORAGE_KEY);
      } catch {
        // ignore
      }
    }
  }

  private handleIncomingMessage(payload: unknown): void {
    if (!payload || typeof payload !== 'object') return;
    const event = payload as CoordinationEvent & { fromTabId?: string };
    if (!event.callSessionId) return;
    if (event.type === 'action' && event.fromTabId === this.tabId) return;
    if (event.type === 'state') {
      this.emitState(String(event.callSessionId));
      return;
    }
    if (event.type === 'action') {
      this.emit({
        type: 'action',
        callSessionId: String(event.callSessionId),
        action: event.action,
        fromTabId: String(event.fromTabId || '')
      });
    }
  }

  private handleStorageEvent = (event: StorageEvent) => {
    if (!event.key) return;

    if (event.key === ACTION_STORAGE_KEY && event.newValue) {
      try {
        const parsed = JSON.parse(event.newValue);
        if (parsed?.fromTabId === this.tabId) return;
        const callSessionId = String(parsed?.callSessionId || '').trim();
        const action = parsed?.action;
        if (!callSessionId) return;
        if (action !== 'accepted' && action !== 'rejected' && action !== 'ended') return;
        this.emit({
          type: 'action',
          callSessionId,
          action,
          fromTabId: String(parsed?.fromTabId || '')
        });
      } catch {
        // ignore
      }
      return;
    }

    if (event.key.startsWith(CLAIM_PREFIX)) {
      const callSessionId = event.key.slice(CLAIM_PREFIX.length);
      if (!callSessionId) return;
      this.emitState(callSessionId);
      if (this.managedCallSessionId === callSessionId) {
        this.ensureClaimForManagedCall();
      }
    }
  };

  private handleVisibilityChange = () => {
    if (this.managedCallSessionId) {
      this.ensureClaimForManagedCall();
    }
  };

  private handlePageHide = () => {
    // Mobile browsers can fire pagehide when the tab is backgrounded or when
    // transient system UI (for example app switching) takes focus. Dropping the
    // claim here can immediately cancel the incoming-call ownership before the
    // user has a chance to answer. Keep the lease and just refresh its hidden
    // visibility; beforeunload and lease expiry still release it safely.
    if (this.managedCallSessionId) {
      this.ensureClaimForManagedCall();
    }
  };

  private handleBeforeUnload = () => {
    if (this.managedCallSessionId) {
      this.stopManagingIncomingCall(this.managedCallSessionId);
    }
  };
}

export const callTabCoordination = new CallTabCoordinationService();
